import {CommentsCategory} from '@domain/comments'
import {useUpmPlant} from '@hconnect/upm'
import {
  Avatar,
  H3,
  Label,
  LabelSmallRegular,
  Table,
  TextKpiMedium,
  TextMedium,
  TextSmallMedium
} from '@hm/fundamental'
import moment from 'moment'
import {forwardRef, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {StaticPartShutdownPrintDrawing} from './StaticPartShutdownPrintDrawing'
import {getAreaSymbol} from './symbols'
import {
  StaticPartShutdownPrintRow,
  useStaticPartShutdownPrintTableConfig
} from './useStaticPartShutdownPrintTableConfig'

import {useFundamentalComments} from '@refra/modules/common/hooks/queries'
import {useKilns, useShutdowns} from '@refra/modules/common/providers'
import {StaticPartsColors} from '@refra/modules/refra-static-parts/enums'
import {useReplacementTotals} from '@refra/modules/refra-static-parts/hooks'
import {
  useStaticPartDetailQuery,
  useStaticPartReplacementQuery
} from '@refra/modules/refra-static-parts/hooks/queries'
import {StaticPartComponentDto} from '@refra/modules/refra-static-parts/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface StaticPartShutdownPrintContentProps {
  selectedComponent: StaticPartComponentDto
  onReady: () => void
}

const formatDate = (date?: string) => (date ? moment(date).format('DD.MM.YYYY, HH:mm') : '')

export const StaticPartShutdownPrintContent = forwardRef<
  HTMLDivElement,
  StaticPartShutdownPrintContentProps
>(({selectedComponent, onReady}, ref) => {
  const {t} = useTranslation()

  const plantCode = useUrlParam('plantCode')
  const staticPartId = useUrlParam('staticPartId')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {selectedKiln} = useKilns()
  const {selectedShutdown} = useShutdowns()

  const shutdownId = selectedShutdown?.id ?? 0

  const {data: staticPart} = useStaticPartDetailQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKiln.upmId,
    staticPartId,
    shutdownId
  })

  const {data: replacements} = useStaticPartReplacementQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKiln.upmId,
    shutdownId,
    componentId: selectedComponent.id,
    enabled: selectedShutdown !== undefined
  })

  const replacement = replacements?.[0]

  const {
    areas,
    totalArea,
    totalWeight,
    isLoading: isTotalsLoading
  } = useReplacementTotals(replacement?.materialInstallations ?? [])

  const comments = useFundamentalComments({
    commentsCategory: CommentsCategory.StaticPartReplacements,
    commentCategoryInstanceIds: [`${selectedKiln.upmId}-${staticPartId}-${shutdownId}`]
  })

  const tableConfig = useStaticPartShutdownPrintTableConfig()

  const tableData: StaticPartShutdownPrintRow[] = areas.flatMap((area) =>
    area.layers.map((layer) => ({
      areaName: t('staticParts.editReplacement.sections.area', {index: area.index + 1}),
      color: StaticPartsColors[area.index % StaticPartsColors.length],
      symbol: getAreaSymbol(area.index),
      layer: layer.layer,
      material: layer.material,
      thickness: layer.thickness,
      weight: layer.weight
    }))
  )

  const isReady =
    !!staticPart && replacements !== undefined && !isTotalsLoading && comments !== undefined

  useEffect(() => {
    if (isReady) onReady()
  }, [isReady, onReady])

  const componentLabel = t(`staticParts.components.${selectedComponent.type}`)
  const title = [upmPlant.name, staticPart?.name, componentLabel].filter(Boolean).join(' | ')
  const savedInfo = replacement?.userInfo
    ? t('staticParts.report.savedBy', {
        date: formatDate(replacement.userInfo.updatedOn),
        person: replacement.userInfo.updatedBy
      })
    : ''

  const renderHeader = (page: string) => (
    <div className="flex flex-row justify-between items-start">
      <div className="flex flex-col gap-1">
        <TextMedium>{title}</TextMedium>
        <LabelSmallRegular>
          {`${t('staticParts.report.shutdown')}: ${moment(
            selectedShutdown?.interventionDate
          ).format('DD.MM.YYYY, HH:mm')}`}
        </LabelSmallRegular>
      </div>
      <div className="flex flex-col items-end gap-1">
        {savedInfo && <LabelSmallRegular>{savedInfo}</LabelSmallRegular>}
        <LabelSmallRegular>{t('staticParts.report.page', {page})}</LabelSmallRegular>
      </div>
    </div>
  )

  return (
    <div ref={ref} data-cy="static_part_shutdown_print_content">
      <div
        className="flex flex-col gap-6 p-6"
        style={{breakAfter: 'page', pageBreakAfter: 'always'}}
      >
        {renderHeader('1/2')}
        <div className="flex flex-row gap-8">
          <Label title={t('staticParts.editReplacement.summary.area')}>
            <TextKpiMedium>{`${totalArea.toFixed(2)} m²`}</TextKpiMedium>
          </Label>
          <Label title={t('staticParts.editReplacement.summary.materialWeight')}>
            <TextKpiMedium>{`${totalWeight.toFixed(2)} t`}</TextKpiMedium>
          </Label>
        </div>
        <div className="flex w-full justify-center">
          <StaticPartShutdownPrintDrawing
            selectedComponent={selectedComponent}
            materialInstallations={replacement?.materialInstallations ?? []}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6 p-6">
        {renderHeader('2/2')}
        <Table
          config={tableConfig}
          data={tableData}
          breakpoint="sm"
          data-cy="static_part_shutdown_print_table"
        />
        <div className="flex flex-col gap-4">
          <H3>{`${t('common.comments')} (${comments?.length ?? 0})`}</H3>
          {comments?.map((comment) => (
            <div key={comment.id} className="grid grid-cols-[24px_1fr] gap-x-2 gap-y-1">
              <div className="self-center">
                <Avatar name={comment.name} />
              </div>
              <div className="flex flex-row items-center gap-2">
                <TextSmallMedium>{comment.name}</TextSmallMedium>
                <span className="text-foreground_faded">&bull;</span>
                <LabelSmallRegular className="text-foreground_faded">
                  {comment.date}
                </LabelSmallRegular>
              </div>
              <div />
              <LabelSmallRegular>{comment.content}</LabelSmallRegular>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
})

StaticPartShutdownPrintContent.displayName = 'StaticPartShutdownPrintContent'
